'use client'

import ConfirmModal from '@/components/confirm-modal'
import { stripHtmlInline } from '@/lib/utils/strip-html'
import { CatalogAssistantPanel } from './catalog-assistant-panel'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    Catalog,
    CATALOG_CATEGORY_LABELS,
    CatalogCategory,
    CatalogType,
    deleteCatalog,
    getCatalogsByCompany,
    getCatalogTypesByOrganization,
    toggleCatalogActive,
    unifyCatalogs,
    bulkDeleteCatalogs,
    catalogCompanyPrice,
} from '@/lib/api/catalog/catalog-api'
import { TussSelector } from '@/components/tuss/TussSelector'
import { CatalogSelector } from '@/components/CatalogSelector'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import {
    useCatalogCategoryLabels,
    useServiceLabel,
} from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdAutoAwesome,
    MdBusiness,
    MdCheckCircle,
    MdClose,
    MdDelete,
    MdDeleteSweep,
    MdEdit,
    MdSell,
    MdEvent,
    MdFileDownload,
    MdFilterList,
    MdHealthAndSafety,
    MdList,
    MdMergeType,
    MdSearch,
    MdStar,
    MdToggleOff,
    MdToggleOn,
    MdVisibility,
    MdInventory2,
} from 'react-icons/md'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

type CategoryTab = 'ALL' | CatalogCategory

interface TabOption {
    key: CategoryTab
    label: string
}

/** Ordem das abas e do dropdown "Adicionar". O rótulo vem do segmento. */
const CATEGORY_TAB_KEYS: CategoryTab[] = [
    'ALL',
    'CONSULTATION',
    'EXAM',
    'PROCEDURE',
    'TELECONSULTATION',
    'PACKAGE',
    'PRODUCT',
]

const CATEGORY_BADGE_CLASS: Record<CatalogCategory, string> = {
    CONSULTATION: 'badge-secondary',
    EXAM: 'badge-info',
    PROCEDURE: 'badge-secondary',
    TELECONSULTATION: 'badge-accent',
    PACKAGE: 'badge-success',
    PRODUCT: 'badge-warning',
}

export default function CatalogListPage() {
    const { user, isPartner, isEmployee, isCompany } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canRead, canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'catalog'
    )
    const {
        allowedCategories,
        canSeeProfessionals,
        canSeePartners,
        canUseAssistant,
    } = useCatalogAccess()
    const [searchInput, setSearchInput] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [toggling, setToggling] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const [showAssistant, setShowAssistant] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    const [professionalFilter, setProfessionalFilter] = useState<number | null>(
        null
    )
    const [showFilters, setShowFilters] = useState(false)
    const [sortBy, setSortBy] = useState<string>('')
    const [typeFilter, setTypeFilter] = useState<number | null>(null)
    const [priceMode, setPriceMode] = useState<string>('')
    const [priceValue, setPriceValue] = useState<string>('')
    const [hasTussFilter, setHasTussFilter] = useState(false)
    const [catalogTypes, setCatalogTypes] = useState<CatalogType[]>([])
    // A aba pode vir na URL (`?category=PACKAGE`): é como a listagem de pacotes manda o
    // operador para cá ao clicar em "Vender pacote". Sem isto ele caía no catálogo inteiro
    // e tinha de achar a aba sozinho.
    const [searchParams] = useSearchParams()
    const [activeTab, setActiveTab] = useState<CategoryTab>(() => {
        const fromUrl = searchParams.get('category')
        return fromUrl && fromUrl in CATALOG_CATEGORY_LABELS
            ? (fromUrl as CategoryTab)
            : 'ALL'
    })

    // ===== Modo de unificação (apenas usuários COMPANY) =====
    /**
     * Unificação TUSS desativada. A barra de ações que liga/desliga o modo está
     * comentada, então não há UI para SAIR dele — e `unifyMode` é persistido no
     * localStorage. Sem esta flag, quem tinha o modo ligado quando a barra foi
     * comentada volta a hidratar `unifyMode: true` a cada carga e fica preso na
     * listagem em modo de unificação, para sempre.
     *
     * Enquanto for `false`: o estado salvo é descartado (e a chave, apagada) e nada
     * novo é persistido. Para reativar, descomente a barra e volte esta flag a `true`.
     */
    const UNIFY_ENABLED = false

    const isCompanyUser = isCompany
    const STORAGE_KEY = `catalog-unify:${user?.companyId ?? 'anon'}`
    const [unifyMode, setUnifyMode] = useState(false)
    const [unifyTuss, setUnifyTuss] = useState<{
        code: string
        term: string
    } | null>(null)
    const [markedItems, setMarkedItems] = useState<
        Array<{ id: number; name: string; category: CatalogCategory }>
    >([])
    const [principalId, setPrincipalId] = useState<number | null>(null)
    // Principal "externo": item JÁ EXISTENTE (tipicamente já unificado) escolhido
    // via selector para anexar novos duplicados a ele. Quando preenchido, ele é o
    // principal (não precisa estar entre os marcados) e TODOS os marcados viram
    // duplicados.
    const [externalPrincipal, setExternalPrincipal] = useState<{
        id: number
        name: string
        tussCode?: string | null
    } | null>(null)
    const [showUnifyModal, setShowUnifyModal] = useState(false)
    const [unifying, setUnifying] = useState(false)
    const [unifyHydrated, setUnifyHydrated] = useState(false)
    // O principal efetivo: o externo (item existente) tem prioridade; senão, o
    // primeiro item marcado na sessão.
    const effectivePrincipalId = externalPrincipal?.id ?? principalId

    // ===== Modo de exclusão em massa =====
    const DELETE_STORAGE_KEY = `catalog-delete:${user?.companyId ?? 'anon'}`
    const [deleteMode, setDeleteMode] = useState(false)
    const [deleteMarked, setDeleteMarked] = useState<
        Array<{ id: number; name: string; category: CatalogCategory }>
    >([])
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [deleteHydrated, setDeleteHydrated] = useState(false)

    // Rótulos de categoria no vocabulário do segmento (Serviço/Avaliação fora da saúde).
    const categoryLabels = useCatalogCategoryLabels()
    const { isHealth, Consultations, Exams, Orders, Teleconsultations } =
        useServiceLabel()

    /** Abas visíveis: só as categorias cujo recurso o usuário vê no menu. */
    const categoryTabs: TabOption[] = useMemo(() => {
        const pluralLabel: Record<CategoryTab, string> = {
            ALL: 'Todos',
            CONSULTATION: Consultations,
            EXAM: Exams,
            PROCEDURE: Orders,
            TELECONSULTATION: Teleconsultations,
            PRODUCT: 'Produtos',
            PACKAGE: 'Pacotes',
        }
        return CATEGORY_TAB_KEYS.filter(
            key => key === 'ALL' || allowedCategories.includes(key)
        ).map(key => ({ key, label: pluralLabel[key] }))
    }, [Consultations, Exams, Orders, Teleconsultations, allowedCategories])

    // `?category=EXAM` na URL de quem não tem Exames cai em "Todos".
    useEffect(() => {
        if (activeTab !== 'ALL' && !allowedCategories.includes(activeTab)) {
            setActiveTab('ALL')
        }
    }, [activeTab, allowedCategories])

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedPrice = useDebounce(priceValue, 500)

    useEffect(() => {
        if (user?.organizationId) {
            const params: { category?: CatalogCategory } = {}
            if (activeTab !== 'ALL') params.category = activeTab
            getCatalogTypesByOrganization(user.organizationId, params).then(
                res => {
                    if (res.success) setCatalogTypes(res.result)
                }
            )
        }
    }, [user?.organizationId, activeTab])

    const priceFilters = useMemo(() => {
        if (!priceMode) return {}
        if (priceMode === 'highest')
            return { sortBy: 'sellingPrice', sortOrder: 'desc' }
        if (priceMode === 'lowest')
            return { sortBy: 'sellingPrice', sortOrder: 'asc' }
        const cents = debouncedPrice
            ? Math.round(parseFloat(debouncedPrice) * 100)
            : undefined
        if (!cents) return {}
        if (priceMode === 'max') return { maxPrice: cents }
        if (priceMode === 'min') return { minPrice: cents }
        if (priceMode === 'exact') return { exactPrice: cents }
        return {}
    }, [debouncedPrice, priceMode])

    const sortFilters = useMemo(() => {
        if (priceMode === 'highest' || priceMode === 'lowest') return {}
        if (sortBy === 'az') return { sortBy: 'name', sortOrder: 'asc' }
        if (sortBy === 'za') return { sortBy: 'name', sortOrder: 'desc' }
        if (sortBy === 'oldest')
            return { sortBy: 'createdAt', sortOrder: 'asc' }
        if (sortBy === 'newest')
            return { sortBy: 'createdAt', sortOrder: 'desc' }
        return {}
    }, [sortBy, priceMode])

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            partnerId: partnerFilter || undefined,
            professionalId: professionalFilter || undefined,
            typeId: typeFilter || undefined,
            category: activeTab !== 'ALL' ? activeTab : undefined,
            // Em "Todos", só as categorias liberadas. Com tudo liberado não manda
            // nada, para não multiplicar chaves de cache à toa.
            categories:
                activeTab === 'ALL' &&
                allowedCategories.length < CATEGORY_TAB_KEYS.length - 1
                    ? allowedCategories
                    : undefined,
            // Listagem do catálogo: sempre ignora o filtro de intermediação
            // (só itens com parceiro). Esse filtro segue valendo nos
            // agendamentos (que não enviam isListing).
            isListing: true,
            hasTuss: hasTussFilter || undefined,
            ...sortFilters,
            ...priceFilters,
        }),
        [
            debouncedSearch,
            partnerFilter,
            professionalFilter,
            typeFilter,
            activeTab,
            allowedCategories,
            hasTussFilter,
            sortFilters,
            priceFilters,
        ]
    )

    const activeFilterCount = [
        typeFilter,
        sortBy,
        priceMode,
        hasTussFilter,
    ].filter(Boolean).length

    const fetchFn = useCallback(
        (params: any) => {
            return getCatalogsByCompany(user!.companyId!, params)
        },
        [user?.companyId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Catalog, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.companyId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    // Hidrata o estado de unificação do localStorage (por empresa).
    useEffect(() => {
        if (!user?.companyId) return

        // Com a unificação desativada, apaga o estado salvo em vez de restaurá-lo:
        // sem a barra de ações não há como sair do modo, e a listagem ficaria presa.
        if (!UNIFY_ENABLED) {
            try {
                localStorage.removeItem(STORAGE_KEY)
            } catch {
                // localStorage indisponível — o estado inicial já é o correto
            }
            setUnifyHydrated(true)
            return
        }

        try {
            const raw = localStorage.getItem(STORAGE_KEY)
            if (raw) {
                const data = JSON.parse(raw)
                setUnifyMode(!!data.unifyMode)
                setUnifyTuss(data.tuss ?? null)
                setMarkedItems(Array.isArray(data.items) ? data.items : [])
                setPrincipalId(data.principalId ?? null)
                setExternalPrincipal(data.externalPrincipal ?? null)
            }
        } catch {
            // localStorage corrompido — ignora
        } finally {
            setUnifyHydrated(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId])

    // Persiste o estado de unificação (só após hidratar, pra não sobrescrever).
    useEffect(() => {
        if (!UNIFY_ENABLED || !unifyHydrated || !user?.companyId) return
        if (
            !unifyMode &&
            markedItems.length === 0 &&
            !unifyTuss &&
            !externalPrincipal
        ) {
            localStorage.removeItem(STORAGE_KEY)
            return
        }
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                unifyMode,
                tuss: unifyTuss,
                items: markedItems,
                principalId,
                externalPrincipal,
            })
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        unifyMode,
        unifyTuss,
        markedItems,
        principalId,
        externalPrincipal,
        unifyHydrated,
    ])

    const isMarked = (id: number) => markedItems.some(m => m.id === id)

    const toggleMark = (item: Catalog) => {
        if (!unifyTuss) {
            toast.warning('Escolha o código TUSS antes de marcar os itens')
            return
        }
        // Não permite marcar o próprio principal externo como duplicado.
        if (externalPrincipal && item.id === externalPrincipal.id) {
            toast.info('Este é o item principal selecionado')
            return
        }
        setMarkedItems(prev => {
            const exists = prev.some(m => m.id === item.id)
            if (exists) {
                const next = prev.filter(m => m.id !== item.id)
                if (!externalPrincipal && principalId === item.id)
                    setPrincipalId(next[0]?.id ?? null)
                return next
            }
            // Sem principal externo, o primeiro marcado vira o principal.
            if (!externalPrincipal && principalId == null)
                setPrincipalId(item.id)
            return [
                ...prev,
                { id: item.id, name: item.name, category: item.category },
            ]
        })
    }

    const exitUnifyMode = () => {
        setUnifyMode(false)
        setUnifyTuss(null)
        setMarkedItems([])
        setPrincipalId(null)
        setExternalPrincipal(null)
        setShowUnifyModal(false)
    }

    const removeMarked = (id: number) => {
        setMarkedItems(prev => {
            const next = prev.filter(m => m.id !== id)
            if (principalId === id) setPrincipalId(next[0]?.id ?? null)
            return next
        })
    }

    const handleUnify = async () => {
        if (!unifyTuss) {
            toast.warning('Escolha o código TUSS')
            return
        }
        const target = effectivePrincipalId
        if (!target) {
            toast.warning('Escolha o item principal')
            return
        }
        const sourceIds = markedItems
            .filter(m => m.id !== target)
            .map(m => m.id)
        if (sourceIds.length === 0) {
            toast.warning('Marque ao menos um item duplicado além do principal')
            return
        }
        setUnifying(true)
        try {
            const res = await unifyCatalogs({
                targetId: target,
                sourceIds,
                tussCode: unifyTuss.code,
            })
            if (res.success) {
                toast.success(
                    `Unificação concluída: ${sourceIds.length} item(ns) movidos para o principal`
                )
                exitUnifyMode()
                reload()
            } else {
                toast.error(res.error || 'Erro ao unificar os itens')
            }
        } catch {
            toast.error('Erro ao unificar os itens')
        } finally {
            setUnifying(false)
        }
    }

    // Hidrata o estado de exclusão em massa do localStorage (por empresa).
    useEffect(() => {
        if (!user?.companyId) return
        try {
            const raw = localStorage.getItem(DELETE_STORAGE_KEY)
            if (raw) {
                const data = JSON.parse(raw)
                setDeleteMode(!!data.deleteMode)
                setDeleteMarked(Array.isArray(data.items) ? data.items : [])
            }
        } catch {
            // ignora
        } finally {
            setDeleteHydrated(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId])

    useEffect(() => {
        if (!deleteHydrated || !user?.companyId) return
        if (!deleteMode && deleteMarked.length === 0) {
            localStorage.removeItem(DELETE_STORAGE_KEY)
            return
        }
        localStorage.setItem(
            DELETE_STORAGE_KEY,
            JSON.stringify({ deleteMode, items: deleteMarked })
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteMode, deleteMarked, deleteHydrated])

    const isDeleteMarked = (id: number) => deleteMarked.some(m => m.id === id)

    const toggleDeleteMark = (item: Catalog) => {
        setDeleteMarked(prev =>
            prev.some(m => m.id === item.id)
                ? prev.filter(m => m.id !== item.id)
                : [
                      ...prev,
                      {
                          id: item.id,
                          name: item.name,
                          category: item.category,
                      },
                  ]
        )
    }

    const removeDeleteMarked = (id: number) =>
        setDeleteMarked(prev => prev.filter(m => m.id !== id))

    const exitDeleteMode = () => {
        setDeleteMode(false)
        setDeleteMarked([])
        setShowBulkDeleteModal(false)
    }

    const handleBulkDelete = async () => {
        if (deleteMarked.length === 0) return
        setBulkDeleting(true)
        try {
            const res = await bulkDeleteCatalogs(deleteMarked.map(m => m.id))
            if (res.success) {
                toast.success(
                    `${res.result?.deleted ?? deleteMarked.length} item(ns) excluídos`
                )
                exitDeleteMode()
                reload()
            } else {
                toast.error(res.error || 'Erro ao excluir os itens')
            }
        } catch {
            toast.error('Erro ao excluir os itens')
        } finally {
            setBulkDeleting(false)
        }
    }

    const handleTabChange = (tab: CategoryTab) => {
        setActiveTab(tab)
        setTypeFilter(null)
    }

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return

        setDeleting(true)
        try {
            const response = await deleteCatalog(itemToDelete)
            if (response.success) {
                toast.success('Item excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir item')
            }
        } catch (error) {
            toast.error('Erro ao excluir item')
        } finally {
            setDeleting(false)
        }
    }

    const handleToggleActive = async (id: number) => {
        setToggling(id)
        try {
            const response = await toggleCatalogActive(id)
            if (response.success) {
                toast.success(
                    response.result.active ? 'Item ativado' : 'Item desativado'
                )
                reload()
            } else {
                toast.error(response.error || 'Erro ao alterar status')
            }
        } catch (error) {
            toast.error('Erro ao alterar status')
        } finally {
            setToggling(null)
        }
    }

    const formatDuration = (minutes?: number) => {
        if (!minutes) return '-'
        if (minutes < 60) return `${minutes} min`
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`
    }

    const formatPrice = (price?: number) => {
        if (!price) return '-'
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price / 100)
    }

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    useEffect(() => {
        if (!loading) setHasLoadedOnce(true)
    }, [loading])

    if (loading && !hasLoadedOnce) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando catálogo...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdList className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Catálogo
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'item' : 'itens'} cadastrados
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* `read`, não `update`: o assistente foi feito justamente para
                        quem tem somente leitura no catálogo — exigir `update` aqui
                        escondia o botão de quem ele veio atender. Os endpoints já
                        pedem apenas `registrations.catalog.read`, e a escrita é
                        cercada por `assertWritable`, markup mínimo e log com
                        desfazer (ver catalog-assistant.controller.ts). */}
                    {canRead && canUseAssistant && (
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowAssistant(true)}
                        >
                            <MdAutoAwesome className="text-xl" />
                            <span className="hidden sm:inline">Assistente</span>
                        </button>
                    )}
                    {/* Só na aba de produto: estoque é coisa de mercadoria, e o botão
                        no meio de exames e consultas só confundiria. */}
                    {canRead && activeTab === 'PRODUCT' && (
                        <button
                            className="btn btn-outline gap-2"
                            onClick={() =>
                                navigate('/registrations/catalog/stock')
                            }
                        >
                            <MdInventory2 className="text-xl" />
                            <span className="hidden sm:inline">Estoque</span>
                        </button>
                    )}
                    {canCreate && (
                        <>
                            {/*
                            <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() => setShowImportModal(true)}
                        >
                            <MdFileDownload className="text-xl" />
                            <span className="hidden sm:inline">Importar</span>
                        </button>
                            */}
                            {/* Dropdown e não botão simples: o formulário abre
                                sempre em Consulta, e o operador de um salão que
                                quer cadastrar um serviço tinha de trocar a
                                categoria à mão toda vez. */}
                            <div className="dropdown dropdown-end">
                                <div
                                    tabIndex={0}
                                    role="button"
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-xl" />
                                    <span className="hidden sm:inline">
                                        Adicionar
                                    </span>
                                </div>
                                <ul
                                    tabIndex={0}
                                    className="dropdown-content menu z-20 mt-2 w-56 rounded-box border border-base-300/50 bg-base-100 p-2 shadow"
                                >
                                    {categoryTabs
                                        .filter(tab => tab.key !== 'ALL')
                                        .map(tab => (
                                            <li key={tab.key}>
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/registrations/catalog/new?category=${tab.key}`
                                                        )
                                                    }
                                                >
                                                    {
                                                        categoryLabels[
                                                            tab.key as CatalogCategory
                                                        ]
                                                    }
                                                </button>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Barra de ações em massa (unificar / excluir) */}
            {/*(isCompanyUser || canDelete) && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-5 flex-wrap">
                            {isCompanyUser && (
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={unifyMode}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                setDeleteMode(false)
                                                setUnifyMode(true)
                                            } else {
                                                exitUnifyMode()
                                            }
                                        }}
                                    />
                                    <span className="font-medium flex items-center gap-1">
                                        <MdMergeType className="text-lg" />{' '}
                                        Unificar duplicados
                                    </span>
                                </label>
                            )}
                            {canDelete && isCompanyUser && (
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-error"
                                        checked={deleteMode}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                setUnifyMode(false)
                                                setDeleteMode(true)
                                            } else {
                                                exitDeleteMode()
                                            }
                                        }}
                                    />
                                    <span className="font-medium flex items-center gap-1">
                                        <MdDeleteSweep className="text-lg" />{' '}
                                        Excluir em massa
                                    </span>
                                </label>
                            )}
                        </div>
                        {unifyMode && (
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn btn-sm btn-outline btn-secondary gap-1"
                                    onClick={() => setShowUnifyModal(true)}
                                >
                                    <MdMergeType className="text-base" />
                                    Ver marcados
                                    <span className="badge badge-sm badge-secondary">
                                        {markedItems.length}
                                    </span>
                                </button>
                                <button
                                    className="btn btn-sm btn-ghost text-error"
                                    onClick={exitUnifyMode}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}
                        {deleteMode && (
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn btn-sm btn-outline btn-error gap-1"
                                    onClick={() => setShowBulkDeleteModal(true)}
                                >
                                    <MdDeleteSweep className="text-base" />
                                    Ver marcados
                                    <span className="badge badge-sm badge-error">
                                        {deleteMarked.length}
                                    </span>
                                </button>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={exitDeleteMode}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}
                    </div>

                    {unifyMode && (
                        <div className="flex flex-col gap-2 pt-3 border-t border-base-300/50">
                            <p className="text-sm text-base-content/70">
                                1) Escolha o <b>código TUSS</b>. 2) Clique nos
                                itens para marcá-los.{' '}
                                {externalPrincipal ? (
                                    <>
                                        Todos os marcados serão unificados no
                                        item principal selecionado abaixo.
                                    </>
                                ) : (
                                    <>
                                        <b>
                                            O primeiro item marcado é o principal
                                        </b>{' '}
                                        (sobrevive e recebe o TUSS); os demais são
                                        unificados nele e desativados. Você pode
                                        trocar o principal em "Ver marcados".
                                    </>
                                )}
                            </p>
                            <TussSelector
                                value={unifyTuss?.code ?? null}
                                onChange={(code, tuss) =>
                                    setUnifyTuss(
                                        code && tuss
                                            ? { code, term: tuss.term }
                                            : null
                                    )
                                }
                                className="max-w-xl"
                            />
                            {!unifyTuss && (
                                <p className="text-xs text-warning">
                                    Escolha o código TUSS para liberar a
                                    marcação dos itens.
                                </p>
                            )}
 
                            <div className="flex flex-col gap-1 mt-1">
                                <span className="text-xs font-medium text-base-content/60">
                                    Anexar a um item já existente (opcional)
                                </span>
                                <CatalogSelector
                                    value={externalPrincipal?.id ?? null}
                                    label=""
                                    placeholder="Buscar o item principal existente..."
                                    className="max-w-xl"
                                    onChange={(id, cat) => {
                                        if (id && cat) {
                                            setExternalPrincipal({
                                                id,
                                                name: cat.name,
                                                tussCode: cat.tussCode,
                                            })
                                            // Remove dos marcados, se estiver lá.
                                            setMarkedItems(prev =>
                                                prev.filter(m => m.id !== id)
                                            )
                                            // Pré-preenche o TUSS a partir do
                                            // item, se ele já tiver um.
                                            if (cat.tussCode && !unifyTuss) {
                                                setUnifyTuss({
                                                    code: cat.tussCode,
                                                    term: cat.name,
                                                })
                                            }
                                        } else {
                                            setExternalPrincipal(null)
                                        }
                                    }}
                                />
                                {externalPrincipal && (
                                    <p className="text-xs text-success">
                                        Principal: <b>{externalPrincipal.name}</b>{' '}
                                        — os itens marcados serão anexados a ele.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {deleteMode && (
                        <div className="pt-3 border-t border-base-300/50">
                            <p className="text-sm text-base-content/70">
                                Clique nos itens para marcá-los para exclusão.
                                Depois abra <b>"Ver marcados"</b> e confirme
                                para excluir todos de uma vez.
                            </p>
                        </div>
                    )}
                </div>
            )*/}

            {/* Category Tabs */}
            <div>
                <div className="tabs tabs-boxed bg-base-100 shadow-sm border border-base-300/50 border-b-0 p-1">
                    {categoryTabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="bg-base-100 border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                        <div className="flex-1 relative">
                            <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                            <input
                                type="text"
                                placeholder="Buscar por nome ou descrição..."
                                className="input input-bordered w-full pl-10 pr-10"
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                            />
                            {loading && hasLoadedOnce && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 loading loading-spinner loading-xs text-secondary pointer-events-none" />
                            )}
                        </div>
                        {!hidePartnerSelector && canSeePartners && (
                            <PartnerSelector
                                value={partnerFilter}
                                onChange={id => setPartnerFilter(id)}
                                label=""
                                placeholder="Parceiro"
                                className="!w-48"
                            />
                        )}
                        {/* Escondido para o próprio parceiro, pelo mesmo motivo do PartnerSelector:
                        não expor a lista de profissionais da clínica a um usuário parceiro. */}
                        {!hidePartnerSelector && canSeeProfessionals && (
                            <ProfessionalSearchSelector
                                value={professionalFilter}
                                onChange={id => setProfessionalFilter(id)}
                                label=""
                                placeholder="Profissional"
                                className="!w-48"
                            />
                        )}
                        {(partnerFilter || professionalFilter) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setPartnerFilter(null)
                                    setProfessionalFilter(null)
                                }}
                                className="btn btn-sm btn-ghost"
                                title="Limpar parceiro e profissional"
                            >
                                Limpar
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setShowFilters(!showFilters)}
                            className={`btn btn-sm ${showFilters || activeFilterCount > 0 ? 'btn-secondary' : 'btn-ghost'} gap-1`}
                        >
                            <MdFilterList className="text-lg" />
                            <span className="hidden sm:inline">Filtros</span>
                            {activeFilterCount > 0 && (
                                <span className="badge badge-sm badge-secondary">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                        {searchInput && (
                            <button
                                type="button"
                                onClick={() => setSearchInput('')}
                                className="btn btn-ghost btn-sm"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>

                    {showFilters && (
                        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-base-300/50">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-base-content/60">
                                    Ordenar por
                                </label>
                                <select
                                    className="select select-bordered select-sm w-40"
                                    value={sortBy}
                                    onChange={e => setSortBy(e.target.value)}
                                >
                                    <option value="">Padrão</option>
                                    <option value="az">A-Z</option>
                                    <option value="za">Z-A</option>
                                    <option value="newest">Mais novos</option>
                                    <option value="oldest">Mais antigos</option>
                                </select>
                            </div>

                            {catalogTypes.length > 0 && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-base-content/60">
                                        Tipo
                                    </label>
                                    <select
                                        className="select select-bordered select-sm w-44"
                                        value={typeFilter || ''}
                                        onChange={e =>
                                            setTypeFilter(
                                                e.target.value
                                                    ? Number(e.target.value)
                                                    : null
                                            )
                                        }
                                    >
                                        <option value="">Todos os tipos</option>
                                        {catalogTypes.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-medium text-base-content/60">
                                    Preço
                                </label>
                                <select
                                    className="select select-bordered select-sm w-40"
                                    value={priceMode}
                                    onChange={e => {
                                        setPriceMode(e.target.value)
                                        if (!e.target.value) setPriceValue('')
                                    }}
                                >
                                    <option value="">Sem filtro</option>
                                    <option value="highest">Maior preço</option>
                                    <option value="lowest">Menor preço</option>
                                    <option value="max">Até R$</option>
                                    <option value="min">A partir de R$</option>
                                    <option value="exact">Igual a R$</option>
                                </select>
                            </div>

                            {(priceMode === 'max' ||
                                priceMode === 'min' ||
                                priceMode === 'exact') && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-base-content/60">
                                        Valor (R$)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="0,00"
                                        className="input input-bordered input-sm w-32"
                                        value={priceValue}
                                        onChange={e =>
                                            setPriceValue(e.target.value)
                                        }
                                    />
                                </div>
                            )}

                            {/* TUSS é a Tabela 22 da ANS: filtrar por ela não diz nada
                            num salão ou numa oficina. O state nasce `false` e vira
                            `undefined` no payload, então não renderizar basta — não
                            há filtro preso. */}
                            {isHealth && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs font-medium text-base-content/60">
                                        Código TUSS
                                    </label>
                                    <label className="label cursor-pointer justify-start gap-2 h-8">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-sm toggle-secondary"
                                            checked={hasTussFilter}
                                            onChange={e =>
                                                setHasTussFilter(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <span className="text-sm">
                                            Apenas com TUSS
                                        </span>
                                    </label>
                                </div>
                            )}

                            {activeFilterCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSortBy('')
                                        setTypeFilter(null)
                                        setPriceMode('')
                                        setPriceValue('')
                                        setHasTussFilter(false)
                                    }}
                                    className="btn btn-ghost btn-sm text-error"
                                >
                                    Limpar filtros
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {items.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdList className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchInput
                                ? 'Nenhum item encontrado'
                                : 'Nenhum item cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchInput
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando itens ao catálogo da sua empresa'}
                        </p>
                        {!searchInput && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        activeTab === 'ALL'
                                            ? '/registrations/catalog/new'
                                            : `/registrations/catalog/new?category=${activeTab}`
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro item
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map(item => (
                        <div
                            key={item.id}
                            onClick={
                                unifyMode
                                    ? () => toggleMark(item)
                                    : deleteMode
                                      ? () => toggleDeleteMark(item)
                                      : undefined
                            }
                            className={`group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border overflow-hidden transition-all duration-300 ${
                                !item.active ? 'opacity-60' : ''
                            } ${
                                unifyMode
                                    ? 'cursor-pointer hover:shadow-lg ' +
                                      (effectivePrincipalId === item.id
                                          ? 'border-success ring-2 ring-success'
                                          : isMarked(item.id)
                                            ? 'border-secondary ring-2 ring-secondary'
                                            : 'border-base-300/50 hover:border-secondary/40')
                                    : deleteMode
                                      ? 'cursor-pointer hover:shadow-lg ' +
                                        (isDeleteMarked(item.id)
                                            ? 'border-error ring-2 ring-error'
                                            : 'border-base-300/50 hover:border-error/40')
                                      : 'border-base-300/50 hover:shadow-lg'
                            }`}
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {item.name}
                                        </h3>
                                        <span
                                            className={`badge badge-sm ${CATEGORY_BADGE_CLASS[item.category]}`}
                                        >
                                            {categoryLabels[item.category]}
                                        </span>
                                        <span
                                            className={`badge badge-sm ${
                                                item.active
                                                    ? 'badge-success'
                                                    : 'badge-ghost'
                                            }`}
                                        >
                                            {item.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                        {item.tussCode && (
                                            <span
                                                className="badge badge-sm badge-outline gap-1 font-mono"
                                                title="Código TUSS"
                                            >
                                                <MdHealthAndSafety className="text-xs" />
                                                TUSS {item.tussCode}
                                            </span>
                                        )}
                                        {unifyMode &&
                                            effectivePrincipalId ===
                                                item.id && (
                                                <span className="badge badge-sm badge-success gap-1">
                                                    <MdStar className="text-xs" />
                                                    Principal
                                                </span>
                                            )}
                                        {unifyMode &&
                                            isMarked(item.id) &&
                                            effectivePrincipalId !==
                                                item.id && (
                                                <span className="badge badge-sm badge-secondary">
                                                    Marcado
                                                </span>
                                            )}
                                        {deleteMode &&
                                            isDeleteMarked(item.id) && (
                                                <span className="badge badge-sm badge-error gap-1">
                                                    <MdDelete className="text-xs" />
                                                    Excluir
                                                </span>
                                            )}
                                    </div>

                                    {/* A descrição é HTML (editor rich text do
                                        produto); aqui vira texto, senão a lista
                                        mostra `<p>` e `<strong>` ao operador. */}
                                    {item.description && (
                                        <p className="text-sm text-base-content/70 mb-2 line-clamp-1">
                                            {stripHtmlInline(item.description)}
                                        </p>
                                    )}

                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                        {item.catalogType && (
                                            <span className="badge badge-outline">
                                                {item.catalogType.name}
                                            </span>
                                        )}
                                        {item.defaultDuration && (
                                            <div className="flex items-center gap-1 text-base-content/70">
                                                <MdAccessTime className="text-base" />
                                                {formatDuration(
                                                    item.defaultDuration
                                                )}
                                            </div>
                                        )}
                                        {catalogCompanyPrice(
                                            item,
                                            user?.companyId
                                        ).price != null &&
                                            catalogCompanyPrice(
                                                item,
                                                user?.companyId
                                            ).price > 0 && (
                                                <span className="text-base-content/70">
                                                    Custo:{' '}
                                                    {formatPrice(
                                                        Number(
                                                            catalogCompanyPrice(
                                                                item,
                                                                user?.companyId
                                                            ).price
                                                        )
                                                    )}
                                                </span>
                                            )}
                                        {(catalogCompanyPrice(
                                            item,
                                            user?.companyId
                                        ).sellingPrice ?? 0) != null &&
                                            (catalogCompanyPrice(
                                                item,
                                                user?.companyId
                                            ).sellingPrice ?? 0) > 0 && (
                                                <span className="font-semibold text-secondary">
                                                    Venda:{' '}
                                                    {formatPrice(
                                                        Number(
                                                            catalogCompanyPrice(
                                                                item,
                                                                user?.companyId
                                                            ).sellingPrice ?? 0
                                                        )
                                                    )}
                                                </span>
                                            )}
                                        {(catalogCompanyPrice(
                                            item,
                                            user?.companyId
                                        ).markupPercentage ?? 0) != null &&
                                            (catalogCompanyPrice(
                                                item,
                                                user?.companyId
                                            ).markupPercentage ?? 0) > 0 && (
                                                <span className="text-base-content/70">
                                                    {(
                                                        catalogCompanyPrice(
                                                            item,
                                                            user?.companyId
                                                        ).markupPercentage ?? 0
                                                    ).toFixed(1)}
                                                    %
                                                </span>
                                            )}
                                        {item.partners &&
                                            item.partners.length > 0 && (
                                                <div className="flex items-center gap-1 text-base-content/70">
                                                    <MdBusiness className="text-base" />
                                                    <span>
                                                        {item.partners.length}{' '}
                                                        {item.partners
                                                            .length === 1
                                                            ? 'parceiro'
                                                            : 'parceiros'}
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                </div>

                                {unifyMode || deleteMode ? (
                                    <div className="flex items-center pr-2 flex-shrink-0">
                                        {(
                                            unifyMode
                                                ? isMarked(item.id) ||
                                                  effectivePrincipalId ===
                                                      item.id
                                                : isDeleteMarked(item.id)
                                        ) ? (
                                            <MdCheckCircle
                                                className={`text-3xl ${
                                                    deleteMode
                                                        ? 'text-error'
                                                        : effectivePrincipalId ===
                                                            item.id
                                                          ? 'text-success'
                                                          : 'text-secondary'
                                                }`}
                                            />
                                        ) : (
                                            <div className="w-7 h-7 rounded-full border-2 border-base-300" />
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        {/* PACOTE não se agenda: é VENDIDO no próprio
                                            cadastro, com data por componente. Manter o
                                            atalho de agendar aqui devolvia o operador ao
                                            formulário de agendamento — o caminho que a
                                            venda existe para substituir. */}
                                        {item.category === 'PACKAGE' ? (
                                            <button
                                                className="btn btn-ghost btn-xs btn-circle text-primary"
                                                title="Vender este pacote"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/registrations/catalog/${item.id}?sell=1`
                                                    )
                                                }}
                                            >
                                                <MdSell size={16} />
                                            </button>
                                        ) : item.category === 'PRODUCT' ? (
                                            /* MERCADORIA também não se agenda — e até
                                               2026-09 este botão dizia "Agendar" e mandava
                                               `?type=PRODUCT`, que a tela de agendamento
                                               não conhece: caía em CONSULTATION e punha o
                                               produto no lugar do serviço. O produto se
                                               vende no balcão, que reserva estoque e gera
                                               a cobrança. */
                                            <button
                                                className="btn btn-ghost btn-xs btn-circle text-primary"
                                                title="Vender este produto"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/commerce/counter-sale?catalogId=${item.id}`
                                                    )
                                                }}
                                            >
                                                <MdSell size={16} />
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-ghost btn-xs btn-circle text-info"
                                                title="Agendar"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/atendimento/scheduling?type=${item.category}&catalogId=${item.id}`
                                                    )
                                                }}
                                            >
                                                <MdEvent size={16} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/registrations/catalog/${item.id}/details`
                                                )
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Ver detalhes"
                                        >
                                            <MdVisibility className="text-lg" />
                                        </button>
                                        {canUpdate && (
                                            <button
                                                onClick={() =>
                                                    handleToggleActive(item.id)
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title={
                                                    item.active
                                                        ? 'Desativar'
                                                        : 'Ativar'
                                                }
                                                disabled={toggling === item.id}
                                            >
                                                {toggling === item.id ? (
                                                    <span className="loading loading-spinner loading-xs"></span>
                                                ) : item.active ? (
                                                    <MdToggleOn className="text-xl text-success" />
                                                ) : (
                                                    <MdToggleOff className="text-xl text-base-content/50" />
                                                )}
                                            </button>
                                        )}
                                        {canUpdate && (
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/registrations/catalog/${item.id}`
                                                    )
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() =>
                                                    handleDelete(item.id)
                                                }
                                                className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="item"
                        />
                    )}
                </div>
            )}

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.CATALOG}
                moduleLabel="Catálogo"
                fetchItems={async (companyId: number) => {
                    const response = await getCatalogsByCompany(companyId)
                    return response.success ? response.result : []
                }}
                hideFileImport={true}
                renderItem={(item: Catalog) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {categoryLabels[item.category]}
                            {item.catalogType
                                ? ` - ${item.catalogType.name}`
                                : ''}
                            {item.defaultDuration
                                ? ` - ${formatDuration(item.defaultDuration)}`
                                : ''}
                            {catalogCompanyPrice(item, user?.companyId).price
                                ? ` - ${formatPrice(Number(catalogCompanyPrice(item, user?.companyId).price))}`
                                : ''}
                        </p>
                    </div>
                )}
                getItemId={(item: Catalog) => item.id}
                getItemName={(item: Catalog) => item.name}
                onImportComplete={reload}
            />

            <CatalogAssistantPanel
                open={showAssistant}
                onClose={() => setShowAssistant(false)}
                onCatalogChanged={reload}
            />

            {showUnifyModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg flex items-center gap-2 mb-1">
                            <MdMergeType /> Itens marcados para unificação
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {unifyTuss ? (
                                <>
                                    Código TUSS:{' '}
                                    <span className="font-mono">
                                        {unifyTuss.code}
                                    </span>{' '}
                                    — {unifyTuss.term}
                                </>
                            ) : (
                                'Nenhum código TUSS escolhido'
                            )}
                        </p>

                        {markedItems.length === 0 && !externalPrincipal ? (
                            <p className="text-center text-base-content/50 py-8">
                                Nenhum item marcado ainda. Feche e clique nos
                                cards para marcar.
                            </p>
                        ) : (
                            <>
                                <p className="text-xs text-base-content/60 mb-2">
                                    O item <b>principal</b> sobrevive, recebe o
                                    TUSS e é{' '}
                                    <b>renomeado para o termo do código TUSS</b>
                                    {unifyTuss ? ` ("${unifyTuss.term}")` : ''};
                                    os demais são unificados nele (parceiros,
                                    profissionais, materiais, histórico) e
                                    desativados.
                                </p>

                                {/* Principal externo (item existente) fixo no topo */}
                                {externalPrincipal && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg border border-success bg-success/5 mb-2">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate flex items-center gap-2">
                                                <span className="badge badge-success badge-sm gap-1">
                                                    <MdStar className="text-xs" />
                                                    Principal (existente)
                                                </span>
                                                {externalPrincipal.name}
                                            </p>
                                        </div>
                                        <button
                                            className="btn btn-xs btn-ghost text-error"
                                            onClick={() =>
                                                setExternalPrincipal(null)
                                            }
                                            title="Remover principal existente"
                                        >
                                            <MdClose className="text-base" />
                                        </button>
                                    </div>
                                )}

                                <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                                    {[...markedItems]
                                        .sort((a, b) =>
                                            a.id === effectivePrincipalId
                                                ? -1
                                                : b.id === effectivePrincipalId
                                                  ? 1
                                                  : 0
                                        )
                                        .map(m => {
                                            const isPrincipal =
                                                m.id === effectivePrincipalId
                                            return (
                                                <div
                                                    key={m.id}
                                                    className={`flex items-center gap-2 p-3 rounded-lg border ${
                                                        isPrincipal
                                                            ? 'border-success bg-success/5'
                                                            : 'border-base-300/50'
                                                    }`}
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium truncate flex items-center gap-2">
                                                            {isPrincipal && (
                                                                <span className="badge badge-success badge-sm gap-1">
                                                                    <MdStar className="text-xs" />
                                                                    Principal
                                                                </span>
                                                            )}
                                                            {m.name}
                                                        </p>
                                                        <p className="text-xs text-base-content/50">
                                                            {
                                                                categoryLabels[
                                                                    m.category
                                                                ]
                                                            }
                                                        </p>
                                                    </div>
                                                    {!externalPrincipal &&
                                                        !isPrincipal && (
                                                            <button
                                                                className="btn btn-xs btn-ghost gap-1"
                                                                onClick={() =>
                                                                    setPrincipalId(
                                                                        m.id
                                                                    )
                                                                }
                                                                title="Tornar principal"
                                                            >
                                                                <MdStar className="text-base" />
                                                                Tornar principal
                                                            </button>
                                                        )}
                                                    <button
                                                        className="btn btn-xs btn-ghost text-error"
                                                        onClick={() =>
                                                            removeMarked(m.id)
                                                        }
                                                        title="Remover"
                                                    >
                                                        <MdClose className="text-base" />
                                                    </button>
                                                </div>
                                            )
                                        })}
                                </div>
                            </>
                        )}

                        <div className="modal-action">
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => setShowUnifyModal(false)}
                                disabled={unifying}
                            >
                                Fechar
                            </button>
                            <button
                                className="btn btn-sm btn-secondary gap-1"
                                onClick={handleUnify}
                                disabled={
                                    unifying ||
                                    !unifyTuss ||
                                    !effectivePrincipalId ||
                                    markedItems.filter(
                                        m => m.id !== effectivePrincipalId
                                    ).length < 1
                                }
                            >
                                {unifying && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                <MdMergeType className="text-base" />
                                Unificar agora
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => !unifying && setShowUnifyModal(false)}
                    />
                </div>
            )}

            {showBulkDeleteModal && (
                <div className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <h3 className="font-bold text-lg flex items-center gap-2 mb-1 text-error">
                            <MdDeleteSweep /> Excluir itens em massa
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {deleteMarked.length} item(ns) marcados. A ação faz
                            exclusão (soft delete) de todos de uma vez.
                        </p>

                        {deleteMarked.length === 0 ? (
                            <p className="text-center text-base-content/50 py-8">
                                Nenhum item marcado. Feche e clique nos cards
                                para marcar.
                            </p>
                        ) : (
                            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                                {deleteMarked.map(m => (
                                    <div
                                        key={m.id}
                                        className="flex items-center gap-2 p-3 rounded-lg border border-base-300/50"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                                {m.name}
                                            </p>
                                            <p className="text-xs text-base-content/50">
                                                {categoryLabels[m.category]}
                                            </p>
                                        </div>
                                        <button
                                            className="btn btn-xs btn-ghost text-error"
                                            onClick={() =>
                                                removeDeleteMarked(m.id)
                                            }
                                            title="Remover da lista"
                                        >
                                            <MdClose className="text-base" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="modal-action">
                            <button
                                className="btn btn-sm btn-ghost"
                                onClick={() => setShowBulkDeleteModal(false)}
                                disabled={bulkDeleting}
                            >
                                Fechar
                            </button>
                            <button
                                className="btn btn-sm btn-error gap-1"
                                onClick={handleBulkDelete}
                                disabled={
                                    bulkDeleting || deleteMarked.length === 0
                                }
                            >
                                {bulkDeleting && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                <MdDeleteSweep className="text-base" />
                                Excluir {deleteMarked.length} item(ns)
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() =>
                            !bulkDeleting && setShowBulkDeleteModal(false)
                        }
                    />
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir item do catálogo"
                message="Tem certeza de que deseja excluir este item do catálogo? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
